import {
  patternDataKey,
  startsWithAlphabetOrUnderscoreDataKey,
  dataTypePatterns,
  bigqueryReservedKeys,
} from "../constants";
import { EntryError } from "./EntryError";
import { action, observable } from "mobx";

export interface EntryInterface {
  key: string;
  type: string;
  repeated: boolean;
  note: string;
  fields: EntryInterface[];
}

export type EntryInterfaceStringified = { [P in keyof EntryInterface]: string };

type DeepPartial<T> = T extends object
  ? {
      [P in keyof T]?: DeepPartial<T[P]>;
    }
  : T;

export class Entry implements EntryInterface {
  @observable accessor key = "";
  @observable accessor type = "";
  @observable accessor fields: Entry[] = [];
  @observable accessor repeated = false;
  @observable accessor note = "";
  @observable accessor error: EntryError = new EntryError({});

  constructor(entry?: DeepPartial<EntryInterface>) {
    this.key = entry?.key ?? "";
    this.type = entry?.type ?? "";
    this.repeated = entry?.repeated ?? false;
    this.note = entry?.note ?? "";
    this.fields = entry?.fields ? entry.fields.map((_) => new Entry(_)) : [];
  }

  isEmptyDataKey() {
    return this.key.length === 0;
  }

  isValidDataKey() {
    return this.isEmptyDataKey() || this.key.match(patternDataKey) != null;
  }

  isStartsWithAlphabetOrUnderscoreDataKey() {
    return (
      this.isEmptyDataKey() ||
      this.key.match(startsWithAlphabetOrUnderscoreDataKey) != null
    );
  }

  isBigqueryReservedKey() {
    return bigqueryReservedKeys.indexOf(this.key.toLowerCase()) === -1;
  }

  isEmptyType() {
    return this.type.length === 0;
  }

  isValidType() {
    return (
      this.isEmptyType() ||
      dataTypePatterns.findIndex(
        (regexp) =>
          !!this.type && this.type.toUpperCase().match(regexp) != null,
      ) !== -1
    );
  }

  isValidTypeLimit() {
    const match = this.type.match(/^(CHAR|VARCHAR)\((\d+)\)/);
    if (match != null) {
      const size = parseInt(match[2], 10);
      switch (match[1]) {
        case "CHAR":
          return size <= 4096;
        case "VARCHAR":
          return size <= 65535;
        default:
          return true;
      }
    }
    return true;
  }

  isDuplicatedKey(entries: Entry[]) {
    return (
      !this.isEmptyDataKey() &&
      entries.filter(
        (entry) => this.key.toLowerCase() == entry.key.toLowerCase(),
      ).length > 1
    );
  }

  isEmptyFields() {
    if (this.type !== "RECORD") {
      return false;
    }
    return this.fields.length == 0;
  }

  isValidNestLevel(nestLevel: number) {
    return nestLevel < 4;
  }

  @action validate(
    entries: Entry[],
    invalidKeyAllowed: boolean,
    nestLevel: number,
  ) {
    this.error = new EntryError({
      invalidKeyAllowed,
      duplicatedKey: this.isDuplicatedKey(entries),
      emptyKey: this.isEmptyDataKey(),
      invalidKey: !this.isValidDataKey(),
      notStartsWithAlphabetOrUnderscoreKey:
        !this.isStartsWithAlphabetOrUnderscoreDataKey(),
      bigqueryReservedKey: !this.isBigqueryReservedKey(),
      emptyType: this.isEmptyType(),
      invalidType: !this.isValidType(),
      invalidTypeLimit: !this.isValidTypeLimit(),
      emptyFields: this.isEmptyFields(),
      invalidNestLevel: !this.isValidNestLevel(nestLevel),
    });
    for (const field of this.fields) {
      field.validate(
        this.fields,
        invalidKeyAllowed,
        nestLevel + 1 + (field.repeated ? 1 : 0),
      );
    }
  }
}
